import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SurveyForm from "@/components/communication/SurveyForm";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const PublicSurvey = () => {
  const { slug } = useParams<{ slug: string }>();
  const [survey, setSurvey] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [respondentName, setRespondentName] = useState<string | null>(null);
  const [respondentSalutation, setRespondentSalutation] = useState<string | null>(null);

  useEffect(() => {
    if (slug) loadSurvey();
  }, [slug]);

  const loadSurvey = async () => {
    setLoading(true);
    // Try by slug first, then by id
    let { data: s } = await supabase
      .from("surveys")
      .select("*")
      .eq("public_slug", slug!)
      .eq("status", "published")
      .maybeSingle();

    if (!s) {
      const { data: s2 } = await supabase
        .from("surveys")
        .select("*")
        .eq("id", slug!)
        .eq("status", "published")
        .maybeSingle();
      s = s2;
    }

    if (!s) {
      setError("Survey not found or no longer available.");
      setLoading(false);
      return;
    }

    setSurvey(s);

    const { data: qs } = await supabase
      .from("survey_questions")
      .select("*")
      .eq("survey_id", s.id)
      .order("sort_order");

    setQuestions((qs || []).map(q => ({
      ...q,
      options: q.options as string[] | null,
      question_text_sw: (q as any).question_text_sw || null,
      options_sw: (q as any).options_sw || null,
    })));
    setLoading(false);
  };

  // Check if any Swahili content exists
  const hasSwahili = !!(
    survey?.title_sw || survey?.description_sw || questions.some((q: any) => q.question_text_sw)
  );

  const handleSubmit = async (answers: Record<string, string | string[]>) => {
    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id || null;

      // Fetch respondent profile for personalized thank you
      if (userId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, salutation")
          .eq("user_id", userId)
          .maybeSingle();
        if (profile) {
          setRespondentName(profile.full_name);
          setRespondentSalutation(profile.salutation);
        }
      }

      const { error } = await supabase.from("survey_responses").insert({
        survey_id: survey.id,
        respondent_id: userId,
        answers,
      });
      if (error) throw error;
      setSubmitted(true);
      toast.success("Response submitted successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading survey...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card><CardContent className="py-8 text-center text-muted-foreground">{error}</CardContent></Card>
      </div>
    );
  }

  if (submitted) {
    const greeting = respondentName
      ? `Thank you, ${respondentSalutation ? respondentSalutation + " " : ""}${respondentName}!`
      : "Thank you!";
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">{greeting}</h2>
              <p className="text-sm text-muted-foreground">Your response has been recorded.</p>
            </div>
            <div className="flex flex-col gap-3 pt-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.close();
                  // window.close() may not work for directly navigated tabs
                  // Fallback: navigate to about:blank or show message
                  setTimeout(() => {
                    try {
                      window.location.replace("about:blank");
                    } catch {
                      // If about:blank is blocked, navigate away
                      window.location.href = "/";
                    }
                  }, 300);
                }}
              >
                No more response, exit
              </Button>
              <Button
                className="w-full"
                onClick={() => {
                  setSubmitted(false);
                }}
              >
                Submit another response
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 py-8">
      <SurveyForm
        title={survey.title}
        description={survey.description}
        questions={questions}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        titleSw={survey.title_sw}
        descriptionSw={survey.description_sw}
        hasSwahili={hasSwahili}
        releasedBy={survey.released_by}
        inquiryPhone={survey.inquiry_phone}
        publishedAt={survey.published_at}
      />
    </div>
  );
};

export default PublicSurvey;
